/**
 * Fix #2885 (follow-up to story 91, #2831) — what happens to the planner-facing values of a JOB the
 * moment it is marked as Extern.
 *
 * Story 91 disabled five fields (job Operators, initial/intermediate preparation time and operators)
 * and made the task duration column read-only, but kept their values (RN27). The planner reads those
 * persisted values regardless of the mark, so a job configured with 4 operators and 2h of task duration
 * kept being sized with them after being subcontracted. The fix inverts RN27 at the only moment it
 * matters: ticking the checkbox empties everything the planner would otherwise read, after the
 * configurator confirms it in a dialog. Unticking touches nothing.
 */

export const ZERO_TIME = '00:00:00'

const EMPTY_PREPARATION_STAGE = { time: ZERO_TIME, operators: '' }

const isZeroTime = (time) => !time || /^[0:]+$/.test(time)

const isZeroCount = (count) => count === '' || count === null || count === undefined || Number(count) === 0

const isEmptyPreparationStage = (stage) => isZeroTime(stage?.time) && isZeroCount(stage?.operators)

/**
 * Story 146 (S64): "no placement" for the two values the balanced line editor writes. Only an absent
 * value is empty — a line position of `0` IS a position, and an object is a chosen asset. The doubt
 * always resolves towards asking: a confirmation the configurator did not need costs a click, a
 * confirmation he needed and did not get costs him a value without warning.
 */
const isEmptyPlacement = (value) => value === null || value === undefined || value === ''

/**
 * Story 208 (S43): "no data" machine time. A real zero ("00:00:00") is data — it is emptied by the mark, so
 * it has to be confirmed like any other value the mark takes away.
 */
const isEmptyMachineTime = (value) => value === null || value === undefined || value === ''

/**
 * Whether marking this job as Extern would lose nothing: every planner-facing value is already
 * zero or empty, so the mark can be applied without asking.
 *
 * Story 146 (S64/CU3-A3.1): the preferred asset and the position in the balanced line count too. They
 * are emptied by the same mark (RN27), so a job carrying either of them DOES lose something and must be
 * confirmed, even with its operators, preparation and durations already at zero.
 *
 * @param {import('../../generic/utilities/objectStructures/workflow/workflow').JobReference} [jobReference]
 * @returns {boolean}
 */
export const plannerFieldsAlreadyEmpty = (jobReference) => {
  return isZeroCount(jobReference?.operatorQuantity) &&
    isEmptyPreparationStage(jobReference?.jobPreparation?.initialPreparation) &&
    isEmptyPreparationStage(jobReference?.jobPreparation?.intermediatePreparation) &&
    (jobReference?.taskReferences ?? []).every((task) => isZeroTime(task?.theoreticalTime) && isEmptyMachineTime(task?.deviceTime)) &&
    isEmptyPlacement(jobReference?.preferenceDevice) &&
    isEmptyPlacement(jobReference?.positionInLine)
}

/**
 * The jobReference marked as Extern with every planner-facing value emptied, in one update so the
 * editor never holds an external job that the planner would still size.
 *
 * Story 146 (S63/RN27): the placement values go with them. An external job is planned on an external
 * asset, so a preferred asset and a position in the balanced line are values the planner will never
 * read (RN13/RN15); leaving them persisted is how the incoherent "external with a preferred asset"
 * keeps coming back. Unmarking does NOT restore them (RN29).
 *
 * Empty is `null` for both, deliberately: it is the very value the balanced line editor already writes
 * when it unplaces a node (`Balanced.jsx`, `BalancedForm.jsx`) and what travels to Sun, where both
 * columns are nullable. `CLAUDE.md` bans `null` in business logic except at the API boundary, and this
 * is exactly that boundary — a second spelling of "empty" would only make the two editors disagree.
 *
 * @param {import('../../generic/utilities/objectStructures/workflow/workflow').JobReference} jobReference
 * @returns {import('../../generic/utilities/objectStructures/workflow/workflow').JobReference}
 */
export const markExternalEmptyingPlannerFields = (jobReference) => {
  return {
    ...jobReference,
    external: true,
    operatorQuantity: 0,
    preferenceDevice: null,
    positionInLine: null,
    jobPreparation: {
      ...jobReference?.jobPreparation,
      initialPreparation: { ...jobReference?.jobPreparation?.initialPreparation, ...EMPTY_PREPARATION_STAGE },
      intermediatePreparation: { ...jobReference?.jobPreparation?.intermediatePreparation, ...EMPTY_PREPARATION_STAGE }
    },
    // Story 208 (S43/RN6): the machine time goes with the duration; "no data" is null at the API boundary.
    taskReferences: (jobReference?.taskReferences ?? []).map((task) => ({ ...task, theoreticalTime: ZERO_TIME, deviceTime: null }))
  }
}
